import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LogOut, 
  BarChart3, 
  FileText, 
  Upload, 
  RefreshCw, 
  Calendar, 
  Download, 
  Database, 
  Settings,
  AlertTriangle,
  TrendingUp 
} from 'lucide-react';
import phoenicianLogo from '../../assets/PhoenicianLogo.png';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/UI/card";
import { Button } from "@/components/UI/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/UI/tabs";
import { useToast } from "@/components/UI/use-toast";
import { misApi } from '../../services/misApi';
import TransactionUpload from './TransactionUpload';

// Top Navigation Component
const TopNav = ({ onLogout }) => {
  return (
    <nav className="bg-white border-b border-gray-200 shadow-sm">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20">
          <div className="flex items-center space-x-8">
            <img 
              src={phoenicianLogo} 
              alt="Phoenician" 
              className="h-12"
            />
            <div className="h-8 w-px bg-gray-200"></div>
            <span className="text-lg text-gray-700">Management Information System (MIS)</span>
          </div>
          
          <div className="flex items-center">
            <Button
              variant="outline"
              onClick={onLogout}
              className="inline-flex items-center px-6 py-2.5 border-2 border-[#C5A572] text-sm font-medium rounded-lg text-[#C5A572] hover:bg-[#C5A572] hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#C5A572] transition-all duration-200"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Welcome Banner Component
const WelcomeBanner = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const displayName = user?.displayName || user?.username?.split('@')[0] || 'User';
  
  const currentDate = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <div className="bg-[#1a1f2e] rounded-xl mb-8">
      <div className="p-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-semibold text-white mb-2">
              Management Information System
            </h1>
            <p className="text-white/80">Hello {displayName}, welcome to MIS Dashboard</p>
          </div>
          
          <div className="text-white/80 flex items-center">
            <Calendar className="h-5 w-5 mr-2" />
            {currentDate}
          </div>
        </div>
      </div>
    </div>
  );
};

// Report Card Component
const ReportCard = ({ title, description, icon: Icon, onClick }) => {
  return (
    <Card 
      className="overflow-hidden hover:shadow-md transition-all duration-300 cursor-pointer group"
      onClick={onClick}
    >
      <CardHeader className="bg-gray-50 border-b pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold text-gray-800">{title}</CardTitle>
          <div className="p-2 bg-[#1a1f2e] rounded-lg group-hover:bg-[#C5A572] transition-colors duration-300">
            <Icon className="h-5 w-5 text-white" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <p className="text-gray-600 text-sm">{description}</p>
        
        <div className="mt-4 flex justify-end">
          <Button 
            variant="ghost" 
            className="text-[#C5A572] hover:text-[#a68445] p-0 hover:bg-transparent"
          >
            View Report
            <svg 
              className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="2" 
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Financial Reports Section
const FinancialReportsSection = ({ isLoading, availableMonths, reports, navigateToReport, setActiveTab }) => {
  if (isLoading) {
    return (
      <div className="py-12 text-center">
        <RefreshCw className="h-8 w-8 animate-spin mx-auto text-[#C5A572]" />
        <p className="mt-4 text-gray-500">Loading reports...</p>
      </div>
    );
  }
  
  if (availableMonths.length === 0) {
    return (
      <div className="py-12 text-center bg-gray-50 rounded-lg border border-dashed border-gray-300">
        <Database className="h-12 w-12 mx-auto text-gray-400" />
        <h3 className="mt-4 text-lg font-medium text-gray-700">No Data Available</h3>
        <p className="mt-2 text-gray-500 max-w-md mx-auto">
          Please upload transaction data first to generate financial reports.
        </p>
        <Button 
          className="mt-4 bg-[#C5A572] hover:bg-[#a68445]" 
          onClick={() => setActiveTab('upload')}
        >
          Upload Transaction Data
        </Button>
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {reports.map((report, index) => (
        <ReportCard
          key={index}
          title={report.title}
          description={report.description}
          icon={report.icon}
          onClick={() => navigateToReport(report.path, availableMonths[0])}
        />
      ))}
    </div>
  );
};

// Admin Tools Cards
const AdminToolsCards = ({ recalculating, availableMonths, handleRecalculateAllMonths, toast }) => {
  const handleInitializeCategories = async () => {
    try {
      await misApi.initializeCategories();
      toast({
        title: "Success",
        description: "Categories have been initialized successfully",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error initializing categories:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to initialize categories",
        duration: 3000,
      });
    }
  };

  const handleResetAllCategories = async () => {
    try {
      await misApi.initializeExtendedCategories(true);
      toast({
        title: "Success",
        description: "Extended categories have been initialized successfully",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error initializing extended categories:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to initialize extended categories",
        duration: 3000,
      });
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border bg-white shadow-sm">
          <CardHeader className="bg-gray-50 border-b">
            <CardTitle className="text-lg font-semibold flex items-center">
              <RefreshCw className="h-5 w-5 mr-2 text-blue-600" />
              Recalculate Financial Data
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-gray-600 mb-4">
              This will recalculate all profit and loss data for all available months based on the transaction entries. 
              Use this to update "Other Income" categorization and other financial metrics.
            </p>
            <div className="mt-4">
              <Button 
                onClick={handleRecalculateAllMonths}
                disabled={recalculating || availableMonths.length === 0}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {recalculating ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Recalculating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Recalculate All P&L Data
                  </>
                )}
              </Button>
              
              {availableMonths.length === 0 && (
                <p className="text-sm text-amber-600 mt-2">
                  No data available for recalculation. Please upload transaction data first.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card className="border bg-white shadow-sm">
          <CardHeader className="bg-gray-50 border-b">
            <CardTitle className="text-lg font-semibold flex items-center">
              <Settings className="h-5 w-5 mr-2 text-blue-600" />
              System Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-gray-600 mb-4">
              Initialize or reinitialize system categories and configuration settings.
            </p>
            <div className="mt-4 space-y-3">
              <Button 
                onClick={handleInitializeCategories}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white"
              >
                Initialize Categories
              </Button>
              
              <Button 
                onClick={handleResetAllCategories}
                variant="outline"
                className="w-full"
              >
                Reset All Categories
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Card className="border bg-white shadow-sm mt-6">
        <CardHeader className="bg-gray-50 border-b">
          <CardTitle className="text-lg font-semibold flex items-center">
            <Calendar className="h-5 w-5 mr-2 text-blue-600" />
            Available Data Months
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {availableMonths.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-gray-500">No months available. Please upload transaction data first.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {availableMonths.map((month, index) => (
                <div 
                  key={index} 
                  className="p-2 bg-gray-50 rounded border text-center text-sm hover:bg-gray-100"
                >
                  {month}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <h3 className="text-amber-800 font-medium flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2" />
          Important Note
        </h3>
        <p className="text-amber-700 text-sm mt-2">
          Use the recalculation tool with caution. It processes all transaction data and may take some time for large datasets.
          Recalculation will properly categorize "Other Income" accounts (43, 4301, 430101, 45, 4501, 450101, 47, 4701, 470101, 470102, 6531, 653101).
        </p>
      </div>
    </>
  );
};

// Main Dashboard Component
const MISDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('reports');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  
  // Define reports data outside of render for better performance
  const reports = useMemo(() => [
    {
      title: "Profit & Loss",
      description: "View comprehensive profit and loss statements with budget comparison and variance analysis.",
      icon: BarChart3,
      path: "profit-loss"
    },
    {
      title: "P&L Trend",
      description: "View profit and loss trends across months with year-over-year comparisons.",
      icon: TrendingUp,
      path: "profit-loss-trend"
    },
    {
      title: "Trial Balance",
      description: "Review the complete trial balance with all accounts and their current balances.",
      icon: FileText,
      path: "trial-balance"
    },
    {
      title: "Revenue Analysis",
      description: "Detailed breakdown of revenue sources with account-level information.",
      icon: BarChart3,
      path: "revenue-analysis"
    },
    {
      title: "Export Reports",
      description: "Export financial reports to Excel for further analysis or sharing.",
      icon: Download,
      path: "export"
    }
  ], []);
  
  // Fetch available months on load
  useEffect(() => {
    const initializeSystem = async () => {
      setIsLoading(true);
      try {
        // Initialize categories if not already done
        await misApi.initializeCategories();
        
        // Fetch available months
        const response = await misApi.getAvailableMonths();
        setAvailableMonths(response.data.data || []);
      } catch (error) {
        console.error("Error initializing system:", error);
        toast({
          variant: "destructive",
          title: "Initialization Error",
          description: "Could not initialize the system properly",
          duration: 5000,
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeSystem();
  }, [toast]);
  
  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  }, [navigate]);

  const navigateToReport = useCallback((path, month) => {
    if (path === "profit-loss-trend") {
      // Extract year from month string (e.g., "Jan 2025" → "2025")
      const year = month.split(' ')[1] || '2025';
      navigate(`/MIS/${path}?year=${encodeURIComponent(year)}`);
    } else {
      navigate(`/MIS/${path}?month=${encodeURIComponent(month || 'Jan 2025')}`);
    }
  }, [navigate]);

  const handleUploadComplete = useCallback((processedMonths) => {
    // Refresh available months after upload
    misApi.getAvailableMonths()
      .then(response => {
        setAvailableMonths(response.data.data || []);
      })
      .catch(error => {
        console.error("Error fetching months after upload:", error);
      });
    
    // Switch to reports tab
    setActiveTab('reports');
    
    toast({
      title: "Processing Complete",
      description: `Data processed for the following months: ${processedMonths.join(", ")}`,
      duration: 5000,
    });
  }, [toast]);

  // Handle recalculation of all months
  const handleRecalculateAllMonths = useCallback(async () => {
    setRecalculating(true);
    
    try {
      // Get all available months
      const monthsResponse = await misApi.getAvailableMonths();
      const months = monthsResponse.data.data || [];
      
      if (months.length === 0) {
        toast({
          title: "No Data",
          description: "No months found to recalculate",
          duration: 3000,
        });
        setRecalculating(false);
        return;
      }
      
      // Show initial toast
      toast({
        title: "Processing",
        description: `Recalculating data for ${months.length} months...`,
        duration: 2000,
      });
      
      // Process each month
      const results = [];
      for (const month of months) {
        try {
          await misApi.recalculateProfitLoss(month);
          results.push({ month, status: 'success' });
          
          // Show progress toast for every 3rd month (to avoid too many toasts)
          if (results.length % 3 === 0 || results.length === months.length) {
            toast({
              title: "Progress",
              description: `Completed ${results.length} of ${months.length} months`,
              duration: 1000,
            });
          }
        } catch (error) {
          console.error(`Error recalculating ${month}:`, error);
          results.push({ month, status: 'error', message: error.message });
        }
      }
      
      // Show completion toast
      toast({
        title: "Complete",
        description: `Successfully recalculated ${results.filter(r => r.status === 'success').length} of ${months.length} months`,
        duration: 4000,
      });
    } catch (error) {
      console.error("Error in recalculation process:", error);
      toast({
        variant: "destructive",
        title: "Recalculation Error",
        description: "An error occurred during recalculation. Check console for details.",
        duration: 4000,
      });
    } finally {
      setRecalculating(false);
    }
  }, [toast]);

  return (
    <div className="min-h-screen bg-gray-50">
      <TopNav onLogout={handleLogout} />
      
      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <WelcomeBanner />
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white shadow-sm rounded-lg p-1 border">
            <TabsTrigger value="reports" className="px-4 py-2 rounded-md data-[state=active]:bg-[#C5A572] data-[state=active]:text-white">
              Financial Reports
            </TabsTrigger>
            <TabsTrigger value="upload" className="px-4 py-2 rounded-md data-[state=active]:bg-[#C5A572] data-[state=active]:text-white">
              Upload Transaction Data
            </TabsTrigger>
            <TabsTrigger value="admin" className="px-4 py-2 rounded-md data-[state=active]:bg-[#C5A572] data-[state=active]:text-white">
              Admin Tools
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="reports" className="mt-6">
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <div className="mb-6">
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">Financial Reports</h2>
                <p className="text-gray-500">Select a financial report to view detailed information</p>
              </div>
              
              <FinancialReportsSection 
                isLoading={isLoading} 
                availableMonths={availableMonths} 
                reports={reports} 
                navigateToReport={navigateToReport}
                setActiveTab={setActiveTab}
              />
            </div>
          </TabsContent>
          
          <TabsContent value="upload" className="mt-6">
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <div className="mb-6">
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">Upload Transaction Data</h2>
                <p className="text-gray-500">Upload your Excel transaction data to generate financial reports</p>
              </div>
              
              <div className="max-w-2xl mx-auto">
                <TransactionUpload onUploadComplete={handleUploadComplete} />
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="admin" className="mt-6">
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <div className="mb-6">
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">Admin Tools</h2>
                <p className="text-gray-500">Administrative functions for data management and system updates</p>
              </div>
              
              <AdminToolsCards 
                recalculating={recalculating}
                availableMonths={availableMonths}
                handleRecalculateAllMonths={handleRecalculateAllMonths}
                toast={toast}
              />
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default MISDashboard;